import { createContext, useContext } from 'react';
import styled, { css } from 'styled-components';

import { nepal } from '@paradigm/design-system/src/colors';
import { useValueSelectors } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/value-selector-context';

import {
  MyQuoteItem,
  TableQuoteItem,
} from '@paradigm/features/src/unified-rfqs/ui/types';
import KillQuoteButton from '#/components/UnifiedRfqs/rfqs/action/KillQuoteButton';
import useFormattedPrice from '@paradigm/features/src/unified-rfqs/ui/hooks/useFormattedPrice';
import { formatProductQty } from '@paradigm/features/src/unified-rfqs/ui/decimal-format';
import { isDatedRfqLegs } from '@paradigm/features/src/unified-rfqs/ui/rfqs/legs-functions';

import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { isValidDecimal } from '@paradigm/utils/formatDecimal';
import { useRfqContext } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';

interface QuotesRowsProps {
  readonly quotes: readonly TableQuoteItem[];
  /**
   * ATTENTION: side of the quotes, not execution side.
   * So `BUY` if `quotes` are bids, `SELL` for asks.
   */
  readonly side: 'SELL' | 'BUY';
  readonly myQuotes?: readonly MyQuoteItem[];
}

export default function QuotesRows({
  quotes,
  myQuotes,
  side,
}: QuotesRowsProps) {
  return (myQuotes == null
    ? quotes.map((quote) => (
        <QuoteRow key={quote.key} quote={quote} side={side} />
      ))
    : createOurRows({
        allQuotes: quotes,
        myQuotes,
        side,
      })) as unknown as JSX.Element;
}

interface CreateOurRowsParams {
  readonly allQuotes: readonly TableQuoteItem[];
  readonly myQuotes: readonly MyQuoteItem[];
  readonly side: 'SELL' | 'BUY';
}

function createOurRows({ allQuotes, myQuotes, side }: CreateOurRowsParams) {
  const compareRows = side === 'BUY' ? compareBidItems : compareAskItems;

  const rows = createPricedRows({ allQuotes, myQuotes, side })
    .sort(compareRows)
    .map(([, row]) => row);

  return rows;
}

type PricedItem<E> = readonly [price: number, element: E];

function compareBidItems<E>([aPrice]: PricedItem<E>, [bPrice]: PricedItem<E>) {
  return bPrice - aPrice;
}

function compareAskItems<E>([aPrice]: PricedItem<E>, [bPrice]: PricedItem<E>) {
  return aPrice - bPrice;
}

interface CreatePricedRowsParams {
  readonly allQuotes: readonly TableQuoteItem[];
  readonly myQuotes: readonly MyQuoteItem[];
  readonly side: CreateOurRowsParams['side'];
}

type PricedRow = readonly [price: number, row: JSX.Element];

function createPricedRows({
  allQuotes,
  myQuotes,
  side,
}: CreatePricedRowsParams): PricedRow[] {
  return allQuotes.map(
    (quote) =>
      [
        Number.parseFloat(quote.price),
        myQuotes.some((myQuote) => myQuote.price === quote.price) ? (
          <MyQuoteRow key={quote.id} quote={quote} side={side} />
        ) : (
          <QuoteRow key={quote.key} quote={quote} side={side} />
        ),
      ] as const,
  );
}

interface QuoteRowProps {
  quote: TableQuoteItem;
  className?: string;
  screenBestRow?: boolean;
  side: CreateOurRowsParams['side'];
}

export function QuoteRow({
  quote,
  className,
  screenBestRow,
  side,
}: QuoteRowProps) {
  const color = useContext(ColorCtx);
  const { selectPrice, selectQuantity } = useValueSelectors();
  const isMobile = useIsMobile();
  const { rfq } = useRfqContext();
  const productCode = rfq?.product_codes[0];

  const formattedPrice = useFormattedPrice({
    price: quote.price,
    productCode,
    side,
    rfqType: rfq?.type,
    description: rfq?.description,
  });

  const hasQuantity = quote.quantity !== '-';
  const hasPrice = isValidDecimal(quote.price);
  const isOffer = side === 'SELL';
  const displayIV = quote.iv != null && quote.iv !== '' && !isMobile;
  const isDated = rfq == null ? false : isDatedRfqLegs(rfq.legs, productCode);

  const formattedQty = hasQuantity
    ? formatProductQty(quote.quantity, productCode, false, isDated)
    : '-';

  return isOffer ? (
    <RowAsk className={className} data-testid={quote.id}>
      {displayIV && <IVCell>{quote.iv}</IVCell>}
      <ColoredPriceCell
        color={hasPrice && !(screenBestRow ?? false) ? color : undefined}
      >
        <ClickableSpan
          role="button"
          onClick={hasPrice ? () => selectPrice(quote) : undefined}
          aria-label="orderbook ask price"
        >
          {hasPrice ? formattedPrice : '-'}
        </ClickableSpan>
      </ColoredPriceCell>
      <QtyCell>
        <ClickableSpan
          role="button"
          onClick={hasQuantity ? () => selectQuantity(quote) : undefined}
        >
          {formattedQty}
        </ClickableSpan>
      </QtyCell>
    </RowAsk>
  ) : (
    <RowBid className={className} data-testid={quote.id}>
      <QtyCell>
        <ClickableSpan
          role="button"
          onClick={hasQuantity ? () => selectQuantity(quote) : undefined}
        >
          {formattedQty}
        </ClickableSpan>
      </QtyCell>
      <ColoredPriceCell
        color={hasPrice && !(screenBestRow ?? false) ? color : undefined}
      >
        <ClickableSpan
          role="button"
          onClick={hasPrice ? () => selectPrice(quote) : undefined}
          aria-label="orderbook bid price"
        >
          {hasPrice ? formattedPrice : '-'}
        </ClickableSpan>
      </ColoredPriceCell>
      {displayIV && <IVCell>{quote.iv}</IVCell>}
    </RowBid>
  );
}

interface MyQuoteRowProps {
  quote: MyQuoteItem;
  side: CreateOurRowsParams['side'];
}

function MyQuoteRow({ quote, side }: MyQuoteRowProps) {
  const color = useContext(ColorCtx);
  const { selectQuantity, selectPrice } = useValueSelectors();
  const { rfq } = useRfqContext();
  const productCode = rfq?.product_codes[0];
  const isOffer = side === 'SELL';
  const isDated = rfq == null ? false : isDatedRfqLegs(rfq.legs, productCode);

  const formattedPrice = useFormattedPrice({
    price: quote.price,
    productCode,
    side,
    rfqType: rfq?.type,
    description: rfq?.description,
  });

  const formattedQty = formatProductQty(
    quote.quantity,
    productCode,
    false,
    isDated,
  );

  return isOffer ? (
    <RowAsk data-testid={quote.id}>
      <KillCell>
        <KillQuoteButton title="Kill" price={quote.price} side={side} />
      </KillCell>
      <ColoredPriceCell color={color}>
        <ClickableSpan role="button" onClick={() => selectPrice(quote)}>
          {formattedPrice}
        </ClickableSpan>
      </ColoredPriceCell>
      <QtyCell>
        <ClickableSpan role="button" onClick={() => selectQuantity(quote)}>
          {formattedQty}
        </ClickableSpan>
      </QtyCell>
    </RowAsk>
  ) : (
    <RowBid data-testid={quote.id}>
      <QtyCell>
        <ClickableSpan role="button" onClick={() => selectQuantity(quote)}>
          {formattedQty}
        </ClickableSpan>
      </QtyCell>
      <KillCell>
        <KillQuoteButton title="Kill" price={quote.price} side={side} />
      </KillCell>
      <ColoredPriceCell color={color}>
        <ClickableSpan role="button" onClick={() => selectPrice(quote)}>
          {formattedPrice}
        </ClickableSpan>
      </ColoredPriceCell>
    </RowBid>
  );
}
export const RowBid = styled.div`
  display: grid;
  padding: 0.3rem 0.25rem;
  grid-template-columns: 50px 80px 100px 10px 100px;
  grid-template-areas: 'iv desk qty kill price';

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: 0.2rem;
    padding-left: 0.5rem;
    grid-template-columns: 60px 10px auto;
    grid-template-areas: 'qty kill price';

    > * {
      padding: 0 !important;
    }
  }

  > * {
    align-items: center;
    display: flex;
    font-size: 0.8rem;
    padding: 0 9px;
    color: ${nepal};
  }
`;

export const RowAsk = styled.div`
  display: grid;
  padding: 0.3rem 0.25rem;
  grid-template-columns: 100px 10px 80px 80px 70px;
  grid-template-areas: 'price kill qty desk iv';

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: 0.2rem;
    padding-left: 0.5rem;
    grid-template-columns: 60px 10px auto;
    grid-template-areas: 'price kill qty';

    > * {
      padding: 0 !important;
    }
  }

  > * {
    align-items: center;
    display: flex;
    font-size: 0.8rem;
    padding: 0 9px;
    color: ${nepal};
  }
`;

export const QtyCell = styled.span`
  grid-area: qty;
`;

export const PriceCell = styled.span`
  grid-area: price;
`;

export const KillCell = styled.span`
  grid-area: kill;
  padding: 0;
`;

export const IVCell = styled.span`
  grid-area: iv;
`;

const ClickableSpan = styled.span`
  ${({ onClick }) =>
    onClick != null
      ? css`
          cursor: pointer;
          transition: color 0.2s ease-in-out;

          &:hover {
            color: white;
          }
        `
      : css`
          cursor: text;
        `};
`;

export const ColoredPriceCell = styled(PriceCell)<{ color?: string }>`
  color: ${({ color }) => color};
`;

export const ColorCtx = createContext('white');
